/* =============================================================
   Mora v14 Product Flow QC
   Keeps custom Mora product templates aligned with Shopify cart flow:
   - updates hidden variant ID when option selects change
   - updates displayed price
   - caps product-page quantity by variant quantity rules + tracked inventory
   - blocks excessive ticket/product quantities before Ajax add-to-cart fires
   ============================================================= */
(() => {
  const SELECTOR = '[data-mora-v9-product]';

  const asNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getVariantData = (section) => {
    const script = section.querySelector('[data-mora-v9-variant-data]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('Mora product variant data could not be parsed.', error);
      return [];
    }
  };

  const isInventoryLimited = (variant) => {
    return Boolean(variant && variant.inventory_management && variant.inventory_policy !== 'continue');
  };

  const getQuantityCap = (variant) => {
    if (!variant) return { max: 0, remaining: 0, isInventoryLimited: false };

    const rule = variant.quantity_rule || {};
    const ruleMax = rule.max === null || rule.max === undefined || rule.max === '' ? null : asNumber(rule.max, null);
    const inventoryLimited = isInventoryLimited(variant);
    const remainingInventory = inventoryLimited
      ? Math.max(0, asNumber(variant.inventory_quantity, 0) - asNumber(variant.cart_quantity, 0))
      : null;

    let max = ruleMax;
    if (inventoryLimited) {
      max = max === null ? remainingInventory : Math.min(max, remainingInventory);
    }

    return { max, remaining: remainingInventory, isInventoryLimited: inventoryLimited };
  };

  const setError = (section, message) => {
    const wrapper = section.querySelector('.product-form__error-message-wrapper');
    const messageEl = section.querySelector('.product-form__error-message');
    if (!wrapper || !messageEl) return;
    messageEl.textContent = message || '';
    wrapper.hidden = !message;
  };

  const selectedOptions = (selects) => Array.from(selects).map((select) => select.value);

  const findVariant = (variants, selects) => {
    const options = selectedOptions(selects);
    return variants.find((variant) => {
      const variantOptions = variant.options || [];
      return variantOptions.length === options.length && variantOptions.every((value, index) => value === options[index]);
    }) || null;
  };

  const setButtonState = ({ section, variant, button, buttonText, paymentButton, cap, isSupper }) => {
    const min = asNumber((variant.quantity_rule || {}).min, 1);
    const capMax = cap.max;
    const canAdd = Boolean(variant && variant.available && (capMax === null || capMax >= min));

    if (button) {
      button.disabled = !canAdd;
      button.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
    }

    if (buttonText) {
      if (!variant) {
        buttonText.textContent = 'Unavailable';
      } else if (!canAdd) {
        buttonText.textContent = 'Sold Out';
      } else {
        buttonText.textContent = isSupper ? 'Reserve Seat' : 'Add to Cart';
      }
    }

    if (paymentButton) {
      paymentButton.classList.toggle('mora-v9-payment-hidden', !canAdd);
    }

    section.classList.toggle('mora-v9-product-page--unavailable', !canAdd);
  };

  const refreshCartQuantities = async (variants, updateUI) => {
    if (!window.routes || !window.routes.cart_url) return;
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const counts = new Map();
      (cart.items || []).forEach((item) => {
        counts.set(Number(item.variant_id), (counts.get(Number(item.variant_id)) || 0) + Number(item.quantity || 0));
      });
      variants.forEach((variant) => {
        variant.cart_quantity = counts.get(Number(variant.id)) || 0;
      });
      updateUI();
    } catch (error) {
      console.warn('Mora product cart quantity refresh failed.', error);
    }
  };

  const initSection = (section) => {
    if (!section || section.dataset.moraV14Ready === 'true') return;
    section.dataset.moraV14Ready = 'true';

    const variants = getVariantData(section);
    const form = section.querySelector('form[action*="/cart/add"], form[action*="/cart/add.js"]');
    const idInput = form && form.querySelector('input[name="id"]');
    const qtyInput = form && form.querySelector('input[name="quantity"]');
    const selects = section.querySelectorAll('[data-mora-v9-option-select]');
    const price = section.querySelector('[data-mora-v9-price]');
    const inventoryNote = section.querySelector('[data-mora-v9-inventory]');
    const button = form && form.querySelector('[name="add"]');
    const buttonText = button && (button.querySelector('[data-mora-v9-add-text]') || button.querySelector('span'));
    const paymentButton = section.querySelector('[data-mora-v9-payment-button]');
    const isSupper = (section.dataset.productHandle || '').includes('supper');

    if (!variants.length || !form || !idInput || !qtyInput) return;

    const currentVariant = () => {
      if (selects.length) return findVariant(variants, selects);
      const id = Number(idInput.value);
      return variants.find((variant) => Number(variant.id) === id) || variants[0] || null;
    };

    const updateUI = () => {
      const variant = currentVariant();
      const cap = getQuantityCap(variant);
      const min = asNumber((variant && variant.quantity_rule && variant.quantity_rule.min), 1);
      const step = asNumber((variant && variant.quantity_rule && variant.quantity_rule.increment), 1);

      setError(section, '');

      if (variant) {
        idInput.value = variant.id;
        idInput.dataset.selectedId = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (price && variant && variant.price) {
        price.textContent = variant.price;
      }

      qtyInput.min = String(min);
      qtyInput.step = String(step);

      if (cap.max === null || cap.max === undefined || cap.max === '') {
        qtyInput.removeAttribute('max');
      } else {
        qtyInput.max = String(Math.max(0, cap.max));
      }

      let qty = asNumber(qtyInput.value, min);
      if (qty < min) qty = min;
      if (cap.max !== null && cap.max !== undefined && cap.max !== '' && qty > cap.max) {
        qty = Math.max(0, cap.max);
      }
      qtyInput.value = String(qty);

      if (inventoryNote) {
        if (!variant) {
          inventoryNote.textContent = 'This selection is unavailable.';
        } else if (!variant.available || (cap.max !== null && cap.max < min)) {
          inventoryNote.textContent = isSupper
            ? 'No seats are available for this ticket selection.'
            : 'This option is currently sold out.';
        } else if (cap.isInventoryLimited && cap.remaining !== null) {
          inventoryNote.textContent = isSupper
            ? `${cap.remaining} seat${cap.remaining === 1 ? '' : 's'} available for this ticket selection.`
            : `${cap.remaining} available for this option.`;
        } else if ((variant.quantity_rule || {}).max) {
          inventoryNote.textContent = `Maximum ${variant.quantity_rule.max} per order for this option.`;
        } else {
          inventoryNote.textContent = '';
        }
      }

      setButtonState({ section, variant, button, buttonText, paymentButton, cap, isSupper });
    };

    selects.forEach((select) => select.addEventListener('change', updateUI));
    qtyInput.addEventListener('input', () => {
      const variant = currentVariant();
      const cap = getQuantityCap(variant);
      const min = asNumber((variant && variant.quantity_rule && variant.quantity_rule.min), 1);
      const qty = asNumber(qtyInput.value, min);
      if (cap.max !== null && cap.max !== undefined && cap.max !== '' && qty > cap.max) {
        qtyInput.value = String(Math.max(0, cap.max));
      }
    });

    form.addEventListener('submit', (event) => {
      const variant = currentVariant();
      const cap = getQuantityCap(variant);
      const min = asNumber((variant && variant.quantity_rule && variant.quantity_rule.min), 1);
      const requested = asNumber(qtyInput.value, min);

      if (!variant || !variant.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setError(section, 'This product option is not available.');
        return;
      }

      if (cap.max !== null && cap.max !== undefined && cap.max !== '' && requested > cap.max) {
        event.preventDefault();
        event.stopImmediatePropagation();
        qtyInput.value = String(Math.max(0, cap.max));
        const label = isSupper ? 'seats' : 'items';
        setError(section, `Only ${cap.max} ${label} are available for this selection.`);
        qtyInput.focus();
        return;
      }

      if (cap.max !== null && cap.max < min) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setError(section, isSupper ? 'No seats are available for this selection.' : 'This option is sold out.');
      }
    }, true);

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => refreshCartQuantities(variants, updateUI));
    }

    updateUI();
  };

  const init = () => document.querySelectorAll(SELECTOR).forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => initSection(event.target.querySelector(SELECTOR)));
})();
